import { watch } from 'vue'

import { useBenchmarkCreativesStore } from '../stores/benchmarkCreativesStore'
import { useCombinationsStore } from '../stores/combinationsStore'
import { useCreativeAiStore } from '../stores/creativeAiStore'
import { useCreativeAiViewStore } from '../stores/creativeAiViewStore'
import { useCreativeLabelEditStore } from '../stores/creativeLabelEditStore'
import { useCreativeLabelOverrideStore } from '../stores/creativeLabelOverrideStore'
import { useLabelSchemaStore } from '../stores/labelSchemaStore'
import { useRecommendationStore } from '../stores/recommendationStore'
import { useWhatsWorkingStore } from '../stores/whatsWorkingStore'

import { useWorkspace } from '@/features/account'
import { registerSessionReset } from '@/features/auth'

/**
 * Module 23's nine stores. Everything except the view store holds analysis, label, benchmark or
 * recommendation data for one account + workspace scope, so both edges wipe all of it —
 * `creativeLabelEditStore` included: an unsaved label edit names a creative id that does not
 * exist in the next tenant.
 *
 * `creativeAiViewStore` is the one split decision. Its `preferences` map is the only persisted
 * slice in the feature (`persist: { key: 'creativeAi:view', pick: ['preferences'] }`) and is
 * already keyed `accountId:workspaceId`, so a workspace switch keeps it (`resetRuntime()`) and
 * the analyst's cards/table choice survives switching away and back; a session reset drops it
 * (`resetState(true)`) because those keys name the previous account. This mirrors what
 * `installCreativeAiProgressLifecycle` already does for the two stores it watches — the resets
 * are idempotent, and registering them here makes the ownership explicit rather than a
 * side-effect of the progress stream being installed.
 */
export function registerCreativeAiLifecycle(): () => void {
  const benchmarks = useBenchmarkCreativesStore()
  const combinations = useCombinationsStore()
  const analyses = useCreativeAiStore()
  const view = useCreativeAiViewStore()
  const labelEdits = useCreativeLabelEditStore()
  const labelOverrides = useCreativeLabelOverrideStore()
  const labelSchema = useLabelSchemaStore()
  const recommendations = useRecommendationStore()
  const whatsWorking = useWhatsWorkingStore()

  const resetScopedData = (): void => {
    benchmarks.reset()
    combinations.resetState()
    analyses.resetState()
    labelEdits.clearAll()
    labelOverrides.reset()
    labelSchema.resetState()
    recommendations.resetState()
    whatsWorking.resetState()
  }

  const { workspaceId } = useWorkspace()
  const stopWorkspace = watch(
    workspaceId,
    (next, previous) => {
      if (String(next ?? '') === String(previous ?? '')) return
      resetScopedData()
      view.resetRuntime()
    },
    { flush: 'sync' },
  )
  const unregisterSession = registerSessionReset(() => {
    resetScopedData()
    view.resetState(true)
  })
  return () => {
    stopWorkspace()
    unregisterSession()
  }
}
